import React, { useMemo } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Form from '@rjsf/antd';
import { MomentInput } from 'moment';
import { SecretConfig } from '@stores/source';
import { UnknownObject } from '@utils/types';
import formWidgets from './widgets';
import { JsonFormWrapper } from './styles';
import { getDefaultUISchema, transformJSONSchema } from './utils';
import { merge } from 'lodash';
import { FormContext } from './formContext';

interface FormError {
  name: string;
  message: string;
}

export interface UISchema {
  [i: string]:
    | {
        'ui:options'?: Record<string, unknown>;
        'ui:widget'?: string;
        'ui:placeholder'?: string;
      }
    | UISchema;
}
export interface JSONSchema {
  type: string;
  airbyte_secret?: unknown;
  properties?: Record<string, JSONSchema>;
  required?: string[];
  secret?: unknown;
  rudder_secret?: unknown;
  examples: MomentInput[];
  isOauth?: boolean;
  title: string;
  order?: number;
  const?: string | number;
  default?: string | number;
  oneOf?: JSONSchema[];
}

interface Props {
  onChange: (data: Record<string, string>, isValid: boolean) => void;
  secretConfig?: SecretConfig;
  formData: Record<string, unknown>;
  schema: JSONSchema;
  disabled?: boolean;
  children?: unknown;
  uiSchema?: UnknownObject;
}
const JSONSchemaForm = React.forwardRef<unknown, Props>(
  (
    { onChange, secretConfig, children, formData = {}, schema, uiSchema = {}, ...formProps }: Props,
    ref,
  ) => {
    const [formValues, setFormValues] = React.useState<Record<string, unknown>>(formData);
    const defaultUiSchema = useMemo(
      () => getDefaultUISchema(schema, secretConfig),
      [schema, secretConfig],
    );
    const sharedState = useMemo(
      () => ({
        formData: formValues,
      }),
      [formValues],
    );
    const transformedSchema = useMemo(
      () => transformJSONSchema(schema, secretConfig),
      [schema, secretConfig],
    );
    return (
      <JsonFormWrapper>
        <FormContext.Provider value={sharedState}>
          <Form
            {...formProps}
            ref={ref}
            liveValidate
            uiSchema={merge(defaultUiSchema, uiSchema)}
            schema={transformedSchema}
            showErrorList={false}
            formData={formData}
            widgets={formWidgets}
            transformErrors={(errors: FormError[]) =>
              errors.map((err) => {
                // we don't need to show error messages for this kind of errors
                if (['required', 'oneOf', 'const'].includes(err.name)) {
                  err.message = '';
                }
                return err;
              })
            }
            onChange={(data: { errors: FormError[]; formData: Record<string, string> }) => {
              const isValid = data.errors.length === 0;
              setFormValues(data.formData);
              onChange(data.formData, isValid);
            }}
          >
            {children || <div />}
          </Form>
        </FormContext.Provider>
      </JsonFormWrapper>
    );
  },
);

export default JSONSchemaForm;
